if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      // [Modified by Codex at 2026-10-19] 量体和附加选色共用资源加载以保证弹窗样式与校验同时就绪
      static customizationAssetPromises = new Map();
      loadGeneration = 0;
      // [End]

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          // [Modified by Codex at 2026-10-19] 变体局部刷新也复用定制组件并处理异步资源失败
          const generation = this.loadGeneration;
          target.addPreProcessCallback((element) => {
            this.preprocessHTML(element).catch((error) => {
              console.error(error);
              if (generation === this.loadGeneration && this.hasAttribute('open')) this.showLoadError(this.openedBy);
            });
          });
          // [End]
        });
      }

      hide(preventFocus = false) {
        // [Modified by Codex at 2026-10-19] 关闭后丢弃仍在加载的旧结果以免覆盖下一次打开的商品
        this.loadGeneration += 1;
        this.resetLoadingOpener();
        // [End]
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        // [Modified by Codex at 2026-10-19] 每次加载绑定独立代次以隔离重开及连续点击的异步结果
        const generation = ++this.loadGeneration;
        this.resetLoadingOpener();
        this.loadingOpener = opener;
        // [End]
        // [Modified by Codex at 2026-10-14] 每次打开独立读取卡片导航路径，避免系列上下文串到其他商品
        this.collectionNavigationUrl = null;
        const cardLink = opener.closest('.product-card-wrapper')?.querySelector('.card__heading a[href]');
        if (cardLink) {
          const navigationUrl = new URL(cardLink.href, window.location.origin);
          if (
            navigationUrl.origin === window.location.origin &&
            /^(?:\/[^/]+)?\/collections\/[^/]+\/products\/[^/]+\/?$/.test(navigationUrl.pathname)
          ) {
            this.collectionNavigationUrl = navigationUrl;
          }
        }
        // [End]
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // [Modified by Codex at 2026-10-19] 组件资源失败时提供完整商品入口以免展示无法完成的购买表单
        return fetch(opener.getAttribute('data-product-url'))
          .then((response) => {
            if (!response.ok) throw new Error('Unable to load the quick-add product.');
            return response.text();
          })
          .then(async (responseText) => {
            if (generation !== this.loadGeneration) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            // [Modified by Codex at 2026-10-16] 优先读取含量体配置的主产品表单并等待外部校验组件
            const productElement = responseHTML.querySelector('product-info[id^="MainProduct-"]') ||
              responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('The quick-add product form is missing.');

            await this.preprocessHTML(productElement);
            // [End]
            if (generation !== this.loadGeneration) return;
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch((error) => {
            if (generation !== this.loadGeneration) return;
            console.error(error);
            this.showLoadError(opener);
            super.show(opener);
          })
          .finally(() => {
            if (generation === this.loadGeneration) this.resetLoadingOpener();
          });
        // [End]
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        // [Modified by Codex at 2026-10-14] 首次加载和变体刷新均让完整详情返回来源系列
        this.updateCollectionDetailsLinks(productElement);
        // [End]
        // [Modified by Codex at 2026-10-19] 同时等待定制样式和脚本以保留完整的弹窗选色及量体体验
        this.simplifyCustomSizeForms(productElement);
        return this.loadCustomizationAssets(productElement);
        // [End]
      }

      // [Modified by Codex at 2026-10-16] 快速购买只保留输入与单位切换，控制器由外部资源幂等初始化
      simplifyCustomSizeForms(productElement) {
        productElement.querySelectorAll('product-custom-size').forEach((component) => {
          component.classList.add('custom-size--compact');
          component.querySelectorAll(
            '.custom-size__overview, .custom-size__help-toggle, .custom-size__help-panel, ' +
            '.custom-size__measurement-help, .custom-size__notes, [data-measurement-dialog]'
          ).forEach((element) => element.remove());
        });
      }

      // [End]

      // [Modified by Codex at 2026-10-19] 两类定制共用幂等加载并在资源失败后允许重新打开重试
      loadCustomizationAssets(productElement) {
        const components = [
          { selector: 'product-custom-size, product-size-chart', name: 'product-custom-size', file: 'product-custom-size' },
          { selector: 'product-more-color', name: 'product-more-color', file: 'product-more-color' },
        ];
        return Promise.all(components.filter(({ selector }) => productElement.querySelector(selector)).map(({ name, file }) => {
          const scripts = Array.from(productElement.querySelectorAll('script[src]')).filter((script) =>
            new URL(script.src, window.location.origin).pathname.endsWith(`/${file}.js`)
          );
          const source = scripts[0]?.src;
          scripts.forEach((script) => script.remove());
          const styles = Array.from(productElement.querySelectorAll('link[rel="stylesheet"][href]')).filter((link) =>
            new URL(link.href, window.location.origin).pathname.endsWith(`/component-${file}.css`)
          );
          const existingStyle = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).find((link) =>
            new URL(link.href, window.location.origin).pathname.endsWith(`/component-${file}.css`) && link.sheet
          );
          const stylesheet = styles[0]?.href;
          styles.forEach((link) => link.remove());
          return Promise.all([
            customElements.get(name) ? Promise.resolve() : this.loadCustomizationAsset(source, 'script', name),
            existingStyle ? Promise.resolve() : this.loadCustomizationAsset(stylesheet, 'link'),
          ]);
        }));
      }

      loadCustomizationAsset(source, tagName, componentName) {
        if (!source) return Promise.reject(new Error('A required quick-add customization asset is missing.'));
        const assets = this.constructor.customizationAssetPromises;
        if (!assets.has(source)) {
          const promise = new Promise((resolve, reject) => {
            const asset = document.createElement(tagName);
            if (tagName === 'script') {
              asset.src = source;
            } else {
              asset.rel = 'stylesheet';
              asset.href = source;
            }
            const fail = () => {
              asset.remove();
              reject(new Error('Unable to load the quick-add customization asset.'));
            };
            asset.onload = () => {
              if (componentName && !customElements.get(componentName)) fail();
              else resolve();
            };
            asset.onerror = fail;
            document.head.appendChild(asset);
          }).catch((error) => {
            assets.delete(source);
            throw error;
          });
          assets.set(source, promise);
        }
        return assets.get(source);
      }

      showLoadError(opener) {
        const message = document.createElement('p');
        message.setAttribute('role', 'alert');
        message.textContent = this.dataset.loadError;
        const link = document.createElement('a');
        link.className = 'button button--secondary';
        link.href = this.collectionNavigationUrl?.href || opener.getAttribute('data-product-url');
        link.textContent = this.dataset.detailsLabel;
        const content = document.createElement('div');
        content.append(message, link);
        this.modalContent.replaceChildren(content);
        if (this.hasAttribute('open')) trapFocus(this, this.querySelector('[role="dialog"]'));
      }

      resetLoadingOpener() {
        if (!this.loadingOpener) return;
        this.loadingOpener.removeAttribute('aria-disabled');
        this.loadingOpener.classList.remove('loading');
        this.loadingOpener.querySelector('.loading__spinner').classList.add('hidden');
        this.loadingOpener = null;
      }
      // [End]

      // [Modified by Codex at 2026-10-14] 仅同商品详情链接继承系列路径并保留当前变体及其他参数
      updateCollectionDetailsLinks(productElement) {
        if (!this.collectionNavigationUrl) return;
        const collectionPath = this.collectionNavigationUrl.pathname.match(
          /^((?:\/[^/]+)?\/collections\/[^/]+\/products\/)([^/]+)\/?$/
        );
        if (!collectionPath) return;

        productElement.querySelectorAll('.product__view-details[href]').forEach((link) => {
          const detailsUrl = new URL(link.getAttribute('href'), window.location.origin);
          const productPath = detailsUrl.pathname.match(/\/products\/([^/]+)\/?$/);
          if (detailsUrl.origin !== window.location.origin || productPath?.[1] !== collectionPath[2]) return;

          detailsUrl.pathname = collectionPath[1] + collectionPath[2];
          if (!detailsUrl.searchParams.has('variant') && this.collectionNavigationUrl.searchParams.has('variant')) {
            detailsUrl.searchParams.set('variant', this.collectionNavigationUrl.searchParams.get('variant'));
          }
          link.setAttribute('href', detailsUrl.pathname + detailsUrl.search + detailsUrl.hash);
        });
      }
      // [End]

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
